import React, { useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import { ButtonV3Icon } from '../../components/Button/ButtonV3Icon';
import type { GraphModalInstance } from '../../components/GraphModal/GraphModal';
import { GraphModal } from '../../components/GraphModal/GraphModal';
import { ReactComponent as DotIcon } from '../../icons/icon-vertical-dots.inline.svg';
import { useAppSelector } from '../../redux/hooks';
import { isBrowser } from '../../utils/web';
import { CounterNestedMsg } from '../ThreadMessage/CounterNestedMsg';
import { getStateMessageStrict } from '../ThreadMessage/thread-slice-utils';

const messages = defineMessages({
  edition: {
    defaultMessage: 'Edition',
    id: '5e7ik+',
  },
  moderation: {
    defaultMessage: 'Modération',
    id: 'SUci9+',
  },
  administration: {
    defaultMessage: 'Administration',
    id: 'f+sh2j',
  },
  popupTitleTopic: {
    defaultMessage: 'Sujet',
    id: 'kmlMYg',
  },
  popupTitleMessage: {
    defaultMessage: 'Message n° {position}',
    id: 'Xj/9Yg',
  },
});

export const ThreadOptionMenu = ({
  messageId,
  admin,
  edition,
  moderator,
}: {
  messageId?: string | undefined;
  admin?: React.ReactNode;
  edition?: React.ReactNode;
  moderator?: React.ReactNode;
}) => {
  const messageNumber = useAppSelector(s => {
    if (!messageId) return undefined;
    const msg = getStateMessageStrict(s.threads.messages, messageId, 'ThreadOptionMenu 1');
    if (!msg.parentMessageId) return 1;
    const parent = getStateMessageStrict(
      s.threads.messages,
      msg.parentMessageId,
      'ThreadOptionMenu 2',
    );
    const i = parent.childMessagesIds?.findIndex(id => id === messageId);
    return i == null ? -1 : i + 1;
  });
  const content = useCallback(
    (tip: GraphModalInstance) => (
      <>
        <div className="bg-gray-51 px-3 py-1 flex justify-between items-center">
          <span>
            {messageId ? (
              <FormattedMessage
                {...messages.popupTitleMessage}
                values={{ position: messageNumber }}
              />
            ) : (
              <FormattedMessage {...messages.popupTitleTopic} />
            )}
          </span>
          <CounterNestedMsg messageId={messageId} />
        </div>
        <div className="ThreadOptionMenu flex text-left">
          <div tabIndex={-1} onMouseUp={() => tip?.hide()} className="divide-y divide-gray-100">
            {edition && <div className="my-1.5">{edition}</div>}

            {moderator && (
              <div className="my-1.5">
                <div className="text-gray-lightv4 px-4 py-1">
                  <FormattedMessage {...messages.moderation} />
                </div>
                {moderator}
              </div>
            )}

            {admin && (
              <div className="my-1.5">
                <div className="text-gray-lightv4 px-4 py-1">
                  <FormattedMessage {...messages.administration} />
                </div>
                {admin}
              </div>
            )}
          </div>
        </div>
      </>
    ),
    [admin, edition, messageId, messageNumber, moderator],
  );

  if (!admin && !edition) return null;

  return (
    <GraphModal
      lazyContent={content}
      placement="bottom-end"
      forcePopover
      appendTo={isBrowser ? document.body : undefined}
      className="!bg-white shadow [&>.tippy-arrow]:before:!border-b-gray-60 [&>.tippy-content]:p-0 text-gray-soft"
    >
      <div>
        <ButtonV3Icon
          variant="outlined"
          radius="rounded-full"
          icon={<DotIcon className="w-5 h-5 text-gray-silver" />}
          className="[&:not(:hover)]:border-transparent"
        />
      </div>
    </GraphModal>
  );
};
